import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Share2, Loader2, Check } from "lucide-react";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Inline button + caption popover that publishes an existing generation
 * (from `content` or `video_tasks`) to the community feed.
 *
 *   <ShareToCommunityButton
 *     sourceCollection="content"   // or "video_tasks"
 *     sourceId={item.id}
 *     defaultCaption=""
 *   />
 */
export const ShareToCommunityButton = ({
  sourceCollection,
  sourceId,
  defaultCaption = "",
  size = "sm",
  variant = "outline",
  className = "",
  testId,
}) => {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [caption, setCaption] = useState(defaultCaption);
  const [submitting, setSubmitting] = useState(false);
  const [shared, setShared] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/community/share`,
        {
          source_collection: sourceCollection,
          source_id: sourceId,
          caption: caption.trim() || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShared(true);
      setOpen(false);
      if (r.data.already_shared) {
        toast.info("Already in your community feed.");
      } else {
        toast.success("Shared to the community 🎉");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not share. Try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (shared) {
    return (
      <Button
        size={size}
        variant="ghost"
        disabled
        data-testid={testId || `community-shared-${sourceId}`}
        className={`text-emerald-600 ${className}`}
      >
        <Check className="w-3 h-3" />
        In community
      </Button>
    );
  }

  return (
    <>
      <Button
        size={size}
        variant={variant}
        onClick={() => setOpen(true)}
        data-testid={testId || `community-share-${sourceId}`}
        className={className}
      >
        <Share2 className="w-3 h-3" />
        Share to community
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/60 backdrop-blur-sm p-4"
          onClick={() => !submitting && setOpen(false)}
          data-testid="community-share-modal"
        >
          <div
            className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-black text-ink-950 mb-2">Share to community</h3>
            <p className="text-sm text-ink-500 mb-4">
              Inspire other creators. Your post will appear in the Community feed for all
              logged-in members. You can remove it anytime.
            </p>
            <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-1 block">
              Add a caption (optional)
            </label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="What inspired this creation?"
              data-testid="community-share-caption"
              className="w-full px-3 py-2 border border-ink-200 rounded-xl text-sm focus:outline-none focus:border-ink-950 resize-none"
            />
            <p className="text-[10px] text-ink-500 mt-1 text-right">{caption.length}/500</p>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 my-4">
              <p className="text-xs text-amber-900 leading-relaxed">
                Posts go through an AI safety check. Adult, hateful, or violent content will
                be blocked. Repeat violations may suspend your community access.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => setOpen(false)}
                disabled={submitting}
                data-testid="community-share-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={submitting}
                data-testid="community-share-confirm"
                className="bg-ink-950 text-white"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Share2 className="w-4 h-4" />
                )}
                Share
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareToCommunityButton;
